// src/components/planner/modals/EventModal.tsx

import React, { useState } from 'react';
import { X, Save } from 'lucide-react';

const API_BASE_URL = '/api';

interface EventModalProps {
    selectedDay: number;
    weekStart: Date;
    onClose: () => void;
    onSave: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const eventCategories = [
    {
        label: 'Aulas & Cursos',
        types: [
            { value: 'class', label: 'Aula' },
            { value: 'course', label: 'Curso' },
            { value: 'tutoring', label: 'Monitoria' },
            { value: 'office_hours', label: 'Plantão de Dúvidas' },
        ]
    },
    {
        label: 'Estudo',
        types: [
            { value: 'individual_study', label: 'Estudo Individual' },
            { value: 'review', label: 'Revisão' },
            { value: 'mock_exam', label: 'Simulado' },
            { value: 'questions', label: 'Questões' },
            { value: 'study_group', label: 'Grupo de Estudo' },
            { value: 'research', label: 'Pesquisa' },
            { value: 'reading', label: 'Leitura' },
        ]
    },
    {
        label: 'Trabalho & Projetos',
        types: [
            { value: 'work', label: 'Trabalho' },
            { value: 'project', label: 'Projeto' },
            { value: 'meeting', label: 'Reunião' },
            { value: 'planning', label: 'Planejamento' },
            { value: 'organization', label: 'Organização' },
        ]
    },
    {
        label: 'Pessoal',
        types: [
            { value: 'gym', label: 'Academia' },
            { value: 'meditation', label: 'Meditação' },
            { value: 'rest', label: 'Descanso' },
            { value: 'family', label: 'Compromisso Familiar' },
            { value: 'social', label: 'Evento Social' },
            { value: 'appointment', label: 'Atendimento' },
        ]
    },
];

const getTypeLabel = (value: string, customType?: string): string => {
    if (value === 'other') return customType || 'Outro';
    for (const cat of eventCategories) {
        const found = cat.types.find(t => t.value === value);
        if (found) return found.label;
    }
    return value;
};

const EventModal: React.FC<EventModalProps> = ({
    selectedDay,
    weekStart,
    onClose,
    onSave,
    onShowToast
}) => {
    const [formData, setFormData] = useState({
        title: '',
        startTime: '',
        endTime: '',
        location: '',
        type: 'class',
        customType: '',
        color: '#003BD1',
        isFixed: true,
        dayOfWeek: selectedDay,
        recurrence: 'fixed' as 'none' | 'fixed' | 'periodic',
        periodicInterval: 14,
        periodicUnit: 'days' as 'days' | 'weeks',
    });

    const [openCategory, setOpenCategory] = useState<string | null>(null);

    const colors = [
        '#003BD1', '#2563eb', '#9333ea', '#ea580c',
        '#dc2626', '#10b981', '#ca8a04', '#6B7280',
        '#86efac', '#93c5fd', '#d8b4fe', '#fdba74',
        '#fca5a5', '#6ee7b7', '#fde68a', '#cbd5e1',
        '#065f46', '#1e3a8a', '#581c87', '#7c2d12',
        '#991b1b', '#0f766e', '#92400e', '#374151',
    ];

    const weekDays = [
        { value: 0, label: 'Domingo' },
        { value: 1, label: 'Segunda-feira' },
        { value: 2, label: 'Terça-feira' },
        { value: 3, label: 'Quarta-feira' },
        { value: 4, label: 'Quinta-feira' },
        { value: 5, label: 'Sexta-feira' },
        { value: 6, label: 'Sábado' },
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            onShowToast('Preencha o título do evento', 'error');
            return;
        }
        if (!formData.startTime || !formData.endTime) {
            onShowToast('Preencha o horário de início e fim', 'error');
            return;
        }
        if (formData.recurrence === 'periodic' && (!formData.periodicInterval || formData.periodicInterval < 1)) {
            onShowToast('Preencha o intervalo de repetição', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/fixed-events`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    title: formData.title,
                    startTime: formData.startTime,
                    endTime: formData.endTime,
                    location: formData.location,
                    color: formData.color,
                    isFixed: formData.recurrence === 'fixed',
                    dayOfWeek: formData.dayOfWeek,
                    weekStart: formData.recurrence === 'none' ? weekStart.toISOString() : null,
                    type: formData.type === 'other' ? 'other' : formData.type,
                    customType: formData.type === 'other' ? formData.customType : undefined,
                    recurrence: formData.recurrence,
                    periodicInterval: formData.recurrence === 'periodic' ? formData.periodicInterval : undefined,
                    periodicUnit: formData.recurrence === 'periodic' ? formData.periodicUnit : undefined,
                })
            });

            if (response.ok) {
                onShowToast('Evento criado', 'success');
                onSave();
            } else {
                onShowToast('Erro ao criar evento', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao criar evento', 'error');
        }
    };

    const chipStyle = (active: boolean) => ({
        padding: '5px 11px',
        borderRadius: '20px',
        border: active ? '2px solid var(--plnr-primary)' : '1px solid var(--plnr-border)',
        background: active ? 'var(--platform-a12)' : 'var(--plnr-bg-hover)',
        color: active ? 'var(--plnr-primary)' : 'var(--plnr-text-primary)',
        fontSize: '12px' as const,
        fontWeight: active ? 700 : 400,
        cursor: 'pointer' as const,
        transition: 'all 0.15s',
        whiteSpace: 'nowrap' as const,
    });

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>Novo Evento</h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="plnr-modal-body">

                        {/* TÍTULO */}
                        <div className="plnr-form-group">
                            <label>Título do evento *</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Ex: Aula de Anatomia"
                                className="plnr-input"
                                required
                            />
                        </div>

                        {/* DIA DA SEMANA */}
                        <div className="plnr-form-group">
                            <label>Dia da semana *</label>
                            <select
                                value={formData.dayOfWeek}
                                onChange={(e) => setFormData({ ...formData, dayOfWeek: Number(e.target.value) })}
                                className="plnr-input"
                            >
                                {weekDays.map(day => (
                                    <option key={day.value} value={day.value}>{day.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* HORÁRIOS + LOCAL */}
                        <div className="plnr-form-row">
                            <div className="plnr-form-group">
                                <label>Início *</label>
                                <input type="time" value={formData.startTime}
                                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                                    className="plnr-input" required />
                            </div>
                            <div className="plnr-form-group">
                                <label>Fim *</label>
                                <input type="time" value={formData.endTime}
                                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                                    className="plnr-input" required />
                            </div>
                            <div className="plnr-form-group">
                                <label>Local</label>
                                <input type="text" value={formData.location}
                                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                    placeholder="Ex: Sala 204" className="plnr-input" />
                            </div>
                        </div>

                        {/* TIPO — seletor em chips categorizados */}
                        <div className="plnr-form-group">
                            <label>Tipo de evento</label>

                            {/* Botão que mostra selecionado e abre/fecha */}
                            <div
                                onClick={() => setOpenCategory(openCategory ? null : eventCategories[0].label)}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '9px 12px',
                                    background: 'var(--plnr-bg-input, var(--plnr-bg-hover))',
                                    border: '1px solid var(--plnr-border)',
                                    borderRadius: openCategory ? '8px 8px 0 0' : '8px',
                                    cursor: 'pointer', fontSize: '13px',
                                    color: 'var(--plnr-text-primary)',
                                    userSelect: 'none' as const,
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{
                                        display: 'inline-block', width: '8px', height: '8px',
                                        borderRadius: '50%', background: 'var(--plnr-primary)', flexShrink: 0
                                    }} />
                                    {getTypeLabel(formData.type, formData.customType || undefined)}
                                </span>
                                <span style={{
                                    fontSize: '10px', color: 'var(--plnr-text-secondary)',
                                    transform: openCategory ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.2s', display: 'inline-block'
                                }}>▼</span>
                            </div>

                            {/* Dropdown de categorias */}
                            {openCategory !== null && (
                                <div style={{
                                    border: '1px solid var(--plnr-border)', borderTop: 'none',
                                    borderRadius: '0 0 8px 8px',
                                    background: 'var(--plnr-bg-card)',
                                    padding: '10px',
                                    display: 'flex', flexDirection: 'column', gap: '6px',
                                }}>
                                    {eventCategories.map(cat => (
                                        <div key={cat.label}>
                                            <button
                                                type="button"
                                                onClick={() => setOpenCategory(openCategory === cat.label ? null : cat.label)}
                                                style={{
                                                    width: '100%', display: 'flex', justifyContent: 'space-between',
                                                    alignItems: 'center', background: 'none', border: 'none',
                                                    padding: '5px 2px', cursor: 'pointer', fontSize: '11px',
                                                    fontWeight: 700, color: 'var(--plnr-text-secondary)',
                                                    textTransform: 'uppercase' as const, letterSpacing: '0.5px',
                                                }}
                                            >
                                                {cat.label}
                                                <span style={{
                                                    fontSize: '10px',
                                                    transform: openCategory === cat.label ? 'rotate(180deg)' : 'none',
                                                    transition: 'transform 0.15s', display: 'inline-block'
                                                }}>▾</span>
                                            </button>

                                            {openCategory === cat.label && (
                                                <div style={{
                                                    display: 'flex', flexWrap: 'wrap', gap: '6px',
                                                    marginTop: '6px', paddingLeft: '4px', paddingBottom: '4px',
                                                }}>
                                                    {cat.types.map(t => (
                                                        <button
                                                            key={t.value}
                                                            type="button"
                                                            onClick={() => {
                                                                setFormData({ ...formData, type: t.value, customType: '' });
                                                                setOpenCategory(null);
                                                            }}
                                                            style={chipStyle(formData.type === t.value)}
                                                        >
                                                            {t.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Outro */}
                                    <div style={{ borderTop: '1px solid var(--plnr-border)', paddingTop: '8px' }}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData({ ...formData, type: 'other' });
                                                setOpenCategory(null);
                                            }}
                                            style={chipStyle(formData.type === 'other')}
                                        >
                                            Outro
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Input customType */}
                            {formData.type === 'other' && (
                                <input
                                    type="text"
                                    value={formData.customType}
                                    onChange={(e) => setFormData({ ...formData, customType: e.target.value })}
                                    placeholder="Descreva o tipo de evento"
                                    className="plnr-input"
                                    style={{ marginTop: '8px' }}
                                />
                            )}
                        </div>

                        {/* CORES */}
                        <div className="plnr-form-group">
                            <label>Cor</label>
                            <div className="plnr-color-grid">
                                {colors.map(color => (
                                    <button
                                        key={color}
                                        type="button"
                                        className={`plnr-color-option ${formData.color === color ? 'active' : ''}`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setFormData({ ...formData, color })}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* RECORRÊNCIA */}
                        <div className="plnr-form-group">
                            <label>Recorrência</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--plnr-text-primary)' }}>
                                    <input type="radio" name="recurrence" checked={formData.recurrence === 'none'}
                                        onChange={() => setFormData({ ...formData, recurrence: 'none', isFixed: false })}
                                        style={{ accentColor: 'var(--plnr-primary)', width: '15px', height: '15px' }} />
                                    Evento único (não repete)
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--plnr-text-primary)' }}>
                                    <input type="radio" name="recurrence" checked={formData.recurrence === 'fixed'}
                                        onChange={() => setFormData({ ...formData, recurrence: 'fixed', isFixed: true })}
                                        style={{ accentColor: 'var(--plnr-primary)', width: '15px', height: '15px' }} />
                                    Fixo semanal (repete toda semana)
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--plnr-text-primary)' }}>
                                    <input type="radio" name="recurrence" checked={formData.recurrence === 'periodic'}
                                        onChange={() => setFormData({ ...formData, recurrence: 'periodic', isFixed: false })}
                                        style={{ accentColor: 'var(--plnr-primary)', width: '15px', height: '15px' }} />
                                    Periódico (intervalo personalizado)
                                </label>

                                {formData.recurrence === 'periodic' && (
                                    <div style={{
                                        marginLeft: '23px', padding: '12px 14px',
                                        background: 'var(--platform-a05)',
                                        border: '1px solid var(--platform-a20)',
                                        borderRadius: '8px', display: 'flex',
                                        alignItems: 'center', gap: '10px', flexWrap: 'wrap' as const,
                                    }}>
                                        <span style={{ fontSize: '13px', color: 'var(--plnr-text-primary)', whiteSpace: 'nowrap' }}>
                                            Repetir a cada
                                        </span>
                                        <input type="number" min={1} max={365} value={formData.periodicInterval}
                                            onChange={(e) => setFormData({ ...formData, periodicInterval: parseInt(e.target.value) || 1 })}
                                            className="plnr-input"
                                            style={{ width: '72px', textAlign: 'center', padding: '6px 8px' }} />
                                        <select value={formData.periodicUnit}
                                            onChange={(e) => setFormData({ ...formData, periodicUnit: e.target.value as 'days' | 'weeks' })}
                                            className="plnr-input" style={{ width: 'auto', padding: '6px 10px' }}>
                                            <option value="days">dias</option>
                                            <option value="weeks">semanas</option>
                                        </select>
                                        <span style={{ fontSize: '11px', color: 'var(--plnr-text-secondary)', width: '100%', marginTop: '2px' }}>
                                            Ex: a cada 15 dias, a cada 2 semanas
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>

                    <div className="plnr-modal-footer">
                        <button type="button" className="plnr-btn secondary" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="plnr-btn primary">
                            <Save size={16} />
                            Salvar Evento
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EventModal;